/**
 * BESS proposal charts.
 *
 * The proposal is rasterised by html2canvas before jsPDF sees it, and html2canvas is only
 * dependable with plain HTML and <img>. So every chart here is drawn on an offscreen canvas
 * and handed back as a PNG data URI — the same shape as the irradiance infographic the
 * template already carries. No Chart.js, nothing to load, nothing to wait for, and the chart
 * cannot half-render because a script had not finished. (user 2026-09-26)
 *
 * Mirrors the charts in the user's own BESS analyzer: the day profile with the cap, monthly
 * MD before/after, kW shaved and payback per unit count, and the 20-year cumulative cashflow.
 */

const BRAND = '#E07A38';          // Astern orange — the proposal's accent
const INK = '#334155';
const MUTED = '#94A3B8';
const GRID = '#E2E8F0';
const BLUE = '#2F5D6E';           // the deck's dark teal
const GREEN = '#15803D';
const GREY = '#CBD5E1';
const SHAVE = 'rgba(224,122,56,0.30)';
const WINDOW = 'rgba(47,93,110,0.07)';   // the 14:00-22:00 MD band
const BODY = 'rgba(47,93,110,0.10)';     // fill under the held-down profile

const FONT = (px, weight = '400') => `${weight} ${px}px Poppins, Arial, Helvetica, sans-serif`;
const rm = (n) => Math.round(Number(n) || 0).toLocaleString('en-US');

/** A 2x canvas so the PNG stays sharp once jsPDF scales it onto the page. */
function surface(w, h) {
    const SCALE = 2;
    const c = document.createElement('canvas');
    c.width = w * SCALE; c.height = h * SCALE;
    const x = c.getContext('2d');
    x.scale(SCALE, SCALE);
    x.fillStyle = '#fff'; x.fillRect(0, 0, w, h);
    return { c, x, w, h };
}

/**
 * A y-axis a person would have drawn: steps of 1, 2, 2.5 or 5 x a power of ten.
 *
 * The axis used to be `max x 1.12` cut into four, which produced 973 / 730 / 487 / 243 on a
 * real day - numbers nobody reads, and they make an otherwise correct chart look like a
 * rough draft. Rounding UP to the step also supplies the headroom the 1.12 was there for.
 * (user 2026-10-26: "just need to be nicer and more accurate")
 */
export function niceAxis(rawMax, targetTicks = 5) {
    const top = rawMax > 0 ? rawMax : 1;
    const rough = top / targetTicks;
    const mag = Math.pow(10, Math.floor(Math.log10(rough)));
    const norm = rough / mag;
    const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
    const niceTop = Math.ceil(top / step) * step;
    return { top: niceTop, step, ticks: Math.max(1, Math.round(niceTop / step)) };
}

/** Axes, gridlines and the y-scale shared by every plot here. */
function frame(x, w, h, { yMax, yTicks = 5, title, yLabel, pad = { l: 52, r: 14, t: 26, b: 30 } }) {
    const plot = { l: pad.l, t: pad.t, r: w - pad.r, b: h - pad.b };
    plot.w = plot.r - plot.l; plot.h = plot.b - plot.t;
    if (title) {
        x.fillStyle = INK; x.font = FONT(12, '600'); x.textAlign = 'left'; x.textBaseline = 'alphabetic';
        x.fillText(title, pad.l - 38, 15);
    }
    const axis = niceAxis(yMax, yTicks);
    const top = axis.top;
    x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'right'; x.textBaseline = 'middle';
    for (let i = 0; i <= axis.ticks; i++) {
        const v = axis.step * i;
        const y = plot.b - (v / top) * plot.h;
        // The baseline reads as an axis, the rest as background.
        x.strokeStyle = i === 0 ? GREY : GRID; x.lineWidth = 1;
        x.beginPath(); x.moveTo(plot.l, y + 0.5); x.lineTo(plot.r, y + 0.5); x.stroke();
        // Years come back as 2.5; kW never does, and rm() would print "3".
        x.fillText(axis.step < 1 || (axis.step % 1) !== 0 ? String(v) : rm(v), plot.l - 6, y);
    }
    if (yLabel) {
        x.save(); x.translate(12, plot.t + plot.h / 2); x.rotate(-Math.PI / 2);
        x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'center'; x.textBaseline = 'middle';
        x.fillText(yLabel, 0, 0); x.restore();
    }
    return { ...plot, top, yOf: (v) => plot.b - (Math.max(0, v) / top) * plot.h };
}

function legend(x, items, xStart, y) {
    let cx = xStart;
    items.forEach(([colour, label]) => {
        x.fillStyle = colour; x.fillRect(cx, y - 6, 10, 8);
        x.fillStyle = INK; x.font = FONT(9); x.textAlign = 'left'; x.textBaseline = 'alphabetic';
        x.fillText(label, cx + 14, y + 1);
        cx += 18 + x.measureText(label).width;
    });
}

const png = (s) => s.c.toDataURL('image/png');

/**
 * The worst demand day: what the meter recorded, and the flat cap the battery holds it to.
 * The shaded wedge between the two IS the kW being bought back.
 */
export function chartDayProfile(series, { capKw, title, yLabel, labelBefore, labelAfter, labelCap, labelShaved, mdFrom = 14, mdTo = 22, w = 620, h = 250 } = {}) {
    if (!series || series.length < 2) return '';
    const s = surface(w, h);
    const { x } = s;
    // No 1.12 inflation: niceAxis rounds up to its own step and supplies the headroom.
    const yMax = Math.max(...series.map(p => p.kw)) * 1.02;
    const f = frame(x, w, h, { yMax, title, yLabel });
    const xOf = (i) => f.l + (i / (series.length - 1)) * f.w;

    /* THE MD WINDOW, shaded. Without it the shaved wedges look arbitrary - they appear in
       three places for no visible reason. The band says WHERE a battery is allowed to help:
       outside 14:00-22:00 on a weekday nothing it does touches the maximum demand charge.
       Drawn first so every line sits on top of it. (user 2026-10-26) */
    const inWin = series.map(p => p.hour >= mdFrom && p.hour < mdTo);
    const firstWin = inWin.indexOf(true);
    const lastWin = inWin.lastIndexOf(true);
    if (firstWin >= 0) {
        x.fillStyle = WINDOW;
        x.fillRect(xOf(firstWin), f.t, Math.max(1, xOf(lastWin) - xOf(firstWin)), f.b - f.t);
    }

    const pts = (key) => series.map((p, i) => ({ x: xOf(i), y: f.yOf(p[key]) }));

    /* A SMOOTH LINE THAT STILL PASSES THROUGH EVERY READING. Half-hourly points at this
       width are about 13px apart, which drew as a visibly angular polyline.
       (user 2026-10-26: "the graph bit rigid isit?")

       Catmull-Rom, so the curve goes THROUGH each recorded point rather than near it -
       a peak must stay where the meter put it, because the number beside the chart is
       that peak. The control points are then clamped to each segment's own y-range: a
       cubic lies inside the hull of its controls, so the curve can never bulge above the
       higher of the two readings it joins. Without that clamp a smoothed line overshoots
       a sharp peak and the chart would claim a demand that was never recorded. */
    const trace = (P) => {
        for (let i = 0; i < P.length - 1; i++) {
            const p0 = P[i - 1] || P[i], p1 = P[i], p2 = P[i + 1], p3 = P[i + 2] || P[i + 1];
            const k = 1 / 6;
            const lo = Math.min(p1.y, p2.y), hi = Math.max(p1.y, p2.y);
            const clamp = (v) => Math.min(hi, Math.max(lo, v));
            x.bezierCurveTo(
                p1.x + (p2.x - p0.x) * k, clamp(p1.y + (p2.y - p0.y) * k),
                p2.x - (p3.x - p1.x) * k, clamp(p2.y - (p3.y - p1.y) * k),
                p2.x, p2.y,
            );
        }
    };

    // Body under the held-down load, so the chart reads as a filled profile rather than
    // two bare strokes.
    const after = pts('after');
    x.beginPath();
    x.moveTo(after[0].x, f.b);
    x.lineTo(after[0].x, after[0].y);
    trace(after);
    x.lineTo(after[after.length - 1].x, f.b);
    x.closePath(); x.fillStyle = BODY; x.fill();

    /* THE SHAVED WEDGES, ONE CLOSED SHAPE PER RUN - never a single polygon across the day.
       It used to be drawn as one path: forward along the recorded load for all 48 points,
       then back along the held-down load. Where the two coincide that path doubles back on
       itself, and canvas fills by NONZERO WINDING, so those doubled-back stretches enclosed
       area BELOW the cap and painted it. Peak shaving only ever removes the part above the
       cap, so that is the only part that may be coloured.
       (user 2026-10-26: "the red portion why go down? cos the peak shaving is only upper
       part") */
    const recorded = pts('kw');
    const shaved = series.map((p) => (p.kw - p.after) > 0.5);
    let run = 0;
    while (run < series.length) {
        if (!shaved[run]) { run++; continue; }
        let end = run;
        while (end + 1 < series.length && shaved[end + 1]) end++;

        /* CLOSE THE WEDGE ON THE CAP, at the exact crossing. Extending it to the
           neighbouring READING instead put the corner wherever the load happened to be
           half an hour earlier - below the cap - so the fill ran down to meet the curve
           there and left a thin red spike under the line. Interpolating the crossing puts
           the corner on the cap where it belongs, and the bottom edge is then a straight
           run along the cap rather than a traced curve. */
        const capY = after[run].y;                 // inside a run, held-down load IS the cap
        const cross = (iIn, iOut) => {
            if (iOut < 0 || iOut >= series.length) return recorded[iIn].x;
            const span = recorded[iOut].y - recorded[iIn].y;
            if (Math.abs(span) < 1e-6) return recorded[iIn].x;
            const t = Math.min(1, Math.max(0, (capY - recorded[iIn].y) / span));
            return recorded[iIn].x + (recorded[iOut].x - recorded[iIn].x) * t;
        };
        const xLeft = cross(run, run - 1);
        const xRight = cross(end, end + 1);

        x.beginPath();
        x.moveTo(xLeft, capY);
        x.lineTo(recorded[run].x, recorded[run].y);
        trace(recorded.slice(run, end + 1));
        x.lineTo(xRight, capY);
        x.closePath(); x.fillStyle = SHAVE; x.fill();
        run = end + 1;
    }

    const line = (P, colour, width, dash) => {
        x.save();
        if (dash) x.setLineDash(dash);
        x.beginPath();
        x.moveTo(P[0].x, P[0].y);
        trace(P);
        x.strokeStyle = colour; x.lineWidth = width; x.lineJoin = 'round'; x.lineCap = 'round'; x.stroke();
        x.restore();
    };
    // Recorded demand DASHED and held-down solid, the way the analyzer draws it: the two
    // coincide outside the window, and a dashed line there reads as "unchanged" instead of
    // hiding the solid one underneath.
    line(recorded, GREY, 1.4, [5, 3]);
    line(after, BLUE, 1.9);

    if (capKw > 0) {
        const y = f.yOf(capKw);
        x.save(); x.setLineDash([4, 3]); x.strokeStyle = GREEN; x.lineWidth = 1.2;
        x.beginPath(); x.moveTo(f.l, y); x.lineTo(f.r, y); x.stroke(); x.restore();
        // Label on the left, where the overnight base leaves the plot empty — on the right it
        // sat on top of the evening peak.
        x.fillStyle = GREEN; x.font = FONT(9, '600'); x.textAlign = 'left'; x.textBaseline = 'bottom';
        x.fillText(`${labelCap || 'Cap'} ${rm(capKw)} kW`, f.l + 4, y - 3);
    }

    // Hour ticks every 3 hours, read off the record's own clock.
    x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'center'; x.textBaseline = 'top';
    series.forEach((p, i) => {
        const hr = Math.round(p.hour);
        if (Math.abs(p.hour - hr) > 0.01 || hr % 3 !== 0) return;
        x.fillText(`${String(hr).padStart(2, '0')}:00`, xOf(i), f.b + 6);
    });
    legend(x, [[GREY, labelBefore || 'Recorded demand'],
        [BLUE, labelAfter || 'With BESS'],
        [SHAVE, labelShaved || 'Shaved']], f.l, h - 4);
    return png(s);
}

/** Monthly maximum demand, before against after — the table in the same breath, as a picture. */
export function chartMonthlyMd(monthly, { title, yLabel, labelBefore, labelAfter, w = 620, h = 230 } = {}) {
    if (!monthly || !monthly.length) return '';
    const s = surface(w, h);
    const { x } = s;
    const yMax = Math.max(...monthly.map(m => m.baseline)) * 1.15;
    const f = frame(x, w, h, { yMax, title, yLabel });
    const slot = f.w / monthly.length;
    const bw = Math.min(26, slot * 0.34);

    monthly.forEach((m, i) => {
        const cx = f.l + slot * (i + 0.5);
        [[m.baseline, BLUE, -bw - 2], [m.after, BRAND, 2]].forEach(([v, colour, dx]) => {
            const y = f.yOf(v);
            x.fillStyle = colour; x.fillRect(cx + dx, y, bw, f.b - y);
        });
        x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'center'; x.textBaseline = 'top';
        x.fillText(m.month, cx, f.b + 6);
        if (m.shaved > 0) {
            x.fillStyle = GREEN; x.font = FONT(9, '600'); x.textBaseline = 'bottom';
            x.fillText(`−${rm(m.shaved)}`, cx, f.yOf(m.baseline) - 3);
        }
    });
    legend(x, [[BLUE, labelBefore || 'MD before'], [BRAND, labelAfter || 'MD after']], f.l, h - 4);
    return png(s);
}

/**
 * Payback per unit count, coloured by whether that step still pays for itself inside the
 * threshold — the analyzer's green/grey rule, on the after-capital-allowance basis.
 */
export function chartPaybackByUnit(ladder, { qualifyYears = 4, chosenUnits, title, yLabel, unitLabel, w = 300, h = 220 } = {}) {
    const rows = (ladder || []).filter(r => Number.isFinite(r.paybackWithCA));
    if (!rows.length) return '';
    const s = surface(w, h);
    const { x } = s;
    const yMax = Math.max(...rows.map(r => r.paybackWithCA), qualifyYears) * 1.2;
    const f = frame(x, w, h, { yMax, title, yLabel, pad: { l: 40, r: 12, t: 26, b: 30 } });
    const slot = f.w / rows.length;
    const bw = Math.min(30, slot * 0.6);

    const yThr = f.yOf(qualifyYears);
    x.save(); x.setLineDash([4, 3]); x.strokeStyle = GREEN; x.lineWidth = 1.2;
    x.beginPath(); x.moveTo(f.l, yThr); x.lineTo(f.r, yThr); x.stroke(); x.restore();

    rows.forEach((r, i) => {
        const cx = f.l + slot * (i + 0.5);
        const y = f.yOf(r.paybackWithCA);
        x.fillStyle = r.qualifies ? GREEN : GREY;
        x.fillRect(cx - bw / 2, y, bw, f.b - y);
        if (r.units === chosenUnits) {
            x.strokeStyle = BRAND; x.lineWidth = 2;
            x.strokeRect(cx - bw / 2 - 1, y - 1, bw + 2, f.b - y + 2);
        }
        x.fillStyle = INK; x.font = FONT(9, '600'); x.textAlign = 'center'; x.textBaseline = 'bottom';
        x.fillText(r.paybackWithCA.toFixed(1), cx, y - 3);
        x.fillStyle = MUTED; x.font = FONT(9); x.textBaseline = 'top';
        x.fillText(`${r.units}${unitLabel ? '' : 'u'}`, cx, f.b + 6);
    });
    return png(s);
}

/** kW shaved per unit count — where the second and third block stop earning their keep. */
export function chartShavedByUnit(ladder, { chosenUnits, title, yLabel, w = 300, h = 220 } = {}) {
    const rows = ladder || [];
    if (!rows.length) return '';
    const s = surface(w, h);
    const { x } = s;
    const yMax = Math.max(...rows.map(r => r.avgShavedKw)) * 1.2;
    const f = frame(x, w, h, { yMax, title, yLabel, pad: { l: 44, r: 12, t: 26, b: 30 } });
    const slot = f.w / rows.length;
    const bw = Math.min(30, slot * 0.6);

    rows.forEach((r, i) => {
        const cx = f.l + slot * (i + 0.5);
        const y = f.yOf(r.avgShavedKw);
        x.fillStyle = r.units === chosenUnits ? BRAND : BLUE;
        x.fillRect(cx - bw / 2, y, bw, f.b - y);
        x.fillStyle = INK; x.font = FONT(9, '600'); x.textAlign = 'center'; x.textBaseline = 'bottom';
        x.fillText(rm(r.avgShavedKw), cx, y - 3);
        x.fillStyle = MUTED; x.font = FONT(9); x.textBaseline = 'top';
        x.fillText(`${r.units}u`, cx, f.b + 6);
    });
    return png(s);
}

/**
 * Twenty years of cumulative cashflow, starting at minus the capital cost. The year the line
 * crosses zero is the payback, drawn rather than asserted.
 */
export function chartCashflow(cashflow, { title, yLabel, labelYear, w = 620, h = 240 } = {}) {
    if (!cashflow || cashflow.length < 2) return '';
    const s = surface(w, h);
    const { x } = s;
    const lo = Math.min(...cashflow), hi = Math.max(...cashflow);
    const span = (hi - lo) || 1;
    const pad = { l: 64, r: 14, t: 26, b: 30 };
    const plot = { l: pad.l, t: pad.t, r: w - pad.r, b: h - pad.b };
    plot.w = plot.r - plot.l; plot.h = plot.b - plot.t;
    const yOf = (v) => plot.b - ((v - lo) / span) * plot.h;

    x.fillStyle = INK; x.font = FONT(12, '600'); x.textAlign = 'left'; x.textBaseline = 'alphabetic';
    if (title) x.fillText(title, pad.l - 50, 15);
    x.strokeStyle = GRID; x.lineWidth = 1;
    x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'right'; x.textBaseline = 'middle';
    for (let i = 0; i <= 4; i++) {
        const v = lo + (span / 4) * i;
        const y = yOf(v);
        x.beginPath(); x.moveTo(plot.l, y + 0.5); x.lineTo(plot.r, y + 0.5); x.stroke();
        x.fillText(rm(v / 1000) + 'k', plot.l - 6, y);
    }
    if (yLabel) {
        x.save(); x.translate(12, plot.t + plot.h / 2); x.rotate(-Math.PI / 2);
        x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'center'; x.textBaseline = 'middle';
        x.fillText(yLabel, 0, 0); x.restore();
    }

    const xOf = (i) => plot.l + (i / (cashflow.length - 1)) * plot.w;
    const zero = yOf(0);
    if (zero > plot.t && zero < plot.b) {
        x.strokeStyle = INK; x.lineWidth = 1;
        x.beginPath(); x.moveTo(plot.l, zero); x.lineTo(plot.r, zero); x.stroke();
    }
    // Fill above zero so the profitable years read at a glance.
    x.beginPath(); x.moveTo(xOf(0), yOf(cashflow[0]));
    cashflow.forEach((v, i) => x.lineTo(xOf(i), yOf(v)));
    x.lineTo(xOf(cashflow.length - 1), zero); x.lineTo(xOf(0), zero); x.closePath();
    x.fillStyle = 'rgba(21,128,61,0.12)'; x.fill();

    x.beginPath();
    cashflow.forEach((v, i) => (i ? x.lineTo(xOf(i), yOf(v)) : x.moveTo(xOf(i), yOf(v))));
    x.strokeStyle = GREEN; x.lineWidth = 2; x.lineJoin = 'round'; x.stroke();

    // Mark the crossing year.
    const cross = cashflow.findIndex(v => v >= 0);
    if (cross > 0) {
        const prev = cashflow[cross - 1], cur = cashflow[cross];
        const frac = cur !== prev ? (0 - prev) / (cur - prev) : 0;
        const cx = xOf(cross - 1 + frac);
        x.fillStyle = BRAND; x.beginPath(); x.arc(cx, zero, 4, 0, Math.PI * 2); x.fill();
        x.font = FONT(9, '600'); x.textAlign = 'center'; x.textBaseline = 'bottom';
        x.fillText(`${(cross - 1 + frac).toFixed(1)} ${labelYear || 'yr'}`, cx, zero - 7);
    }

    x.fillStyle = MUTED; x.font = FONT(9); x.textAlign = 'center'; x.textBaseline = 'top';
    for (let i = 0; i < cashflow.length; i += 5) x.fillText(String(i), xOf(i), plot.b + 6);
    return png(s);
}
